import { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PMRWidget from '@zg-rentals/rg-widgets-pmr';

import { Anchor, Box, Heading } from '@zillow/constellation-10';

// Actions
import { loadFilterFromUrl } from 'app/shared/flux/actions/FilterActions';
import { handlePMRFilterChange } from 'app/shared/flux/actions/FilterActions';
import { useAppDispatch } from 'app/shared/flux/hooks';

// Components
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';
import PageHead from './PageHead';
import HeadingCard from 'app/shared/components/HeadingCard';
import AreaLinks from 'app/shared/components/AreaLinks';
import StaticCommonQuestions from 'app/shared/components/StaticCommonQuestions';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import MarketSummary from 'app/shared/components/MarketSummary';

// Constants
import StringConstants from 'app/shared/constants/StringConstants';

// Misc / utils
import { rentalManagerClient } from 'app/shared/utils/api';
import { queryStringToQueryObject } from 'app/shared/utils/urlUtils';

// Types
import type { PartialFilterType } from 'app/shared/types/Filters';
import type { State } from 'app/shared/types/State';
import { logError } from '@zg-rentals/log-error';

const applyZillowMapControlStyles = () => {
  // @ts-ignore
  import('@zillow/zillow-map-control/dist/ZillowMapControl.less');
};

const PriceMyRentalResultsPage = ({ history }) => {
  const dispatch = useAppDispatch();
  const address = useSelector((state: State) => state.address);
  const comparables = useSelector((state: State) => state.comparables);
  const filter = useSelector((state: State) => state.filter);
  const app = useSelector((state: State) => state.app);
  const ui = useSelector((state: State) => state.ui);
  const user = useSelector((state: State) => state.user);
  const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = useSelector((state: State) => state.router.location.pathname);

  const { url, areaName, beds, summary } = address?.marketSummary ?? {};
  const {
    medianRent = null,
    monthlyChange = null,
    yearlyChange = null,
    avgDaysOnMarket = null,
    availableRentals = null,
  } = summary ?? {};
  const hasMarketSummary = !!(medianRent || monthlyChange || yearlyChange || avgDaysOnMarket || availableRentals);
  const filterDescription =
    beds === 'All beds'
      ? 'all bedrooms and all property types'
      : `${beds !== '0' ? `${beds} bedroom` : 'studio'} properties`;

  useEffect(() => {
    history.listen((location) => {
      Promise.resolve(dispatch(loadFilterFromUrl(queryStringToQueryObject(location.search)))).catch((err) => {
        logError({
          error: err,
          errorType: 'Filter',
          context: {
            details: 'Results page: Error loading filter from url (CSR)',
            root: 'pages/PriceMyRentalResultsPage#useEffect',
            pathAttempted: pathname,
          },
        });
      });
    });
    setIsMounted(true);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  useEffect(applyZillowMapControlStyles, []);

  const isSmallScreen = isMobile || screenWidth === 'md';

  const setFilter = (partialFilterObj?: PartialFilterType) => dispatch(handlePMRFilterChange(partialFilterObj));

  const MarketSummaryHeading = (
    // @ts-ignore
    <Anchor href={url} target="_blank" rel="noopener noreferrer" fontFamily="serif">
      {areaName} Market Summary
    </Anchor>
  );

  return (
    <>
      <PageHead address={address} canonicalURL={`${StringConstants.ZILLOW_URL}${pathname}`} />
      <Box
        data-testid="PriceMyRentalResultsPage"
        css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
      >
        {isMounted ? (
          <Box css={{ width: '100%', maxWidth: '1220px' }}>
            <HeadingCard />
            {hasMarketSummary && (
              <MarketSummary
                heading={MarketSummaryHeading}
                market={areaName}
                filterDescription={filterDescription}
                medianRent={medianRent}
                monthlyChange={monthlyChange}
                yearlyChange={yearlyChange}
                avgDaysOnMarket={avgDaysOnMarket}
                availableRentals={availableRentals}
                isSmallScreen={isSmallScreen}
                elevated={false}
                outlined={false}
              />
            )}
            <Box
              css={{
                background: 'bg.neutral',
                marginBottom: '400',
                padding: '{spacing.600} {spacing.600} {spacing.1000} {spacing.600}',
                marginTop: hasMarketSummary ? '400' : '0',
              }}
              data-testid="pmr-widget"
            >
              <PMRWidget
                address={address}
                app={{ ...app, googleMapsApiKey: app.googleMapsApiKey.key }}
                comparables={comparables}
                filter={filter}
                heading={
                  <Heading level={3} textStyle="responsive.heading-md" css={{ marginBottom: '200' }}>
                    Local rental marketplace
                  </Heading>
                }
                requestClient={rentalManagerClient}
                setFilter={setFilter}
                ui={ui}
                user={user}
              />
              <Box css={{ border: 'none', height: '1px', background: 'bg.soft', margin: 0, marginBottom: '400' }} />
              <AreaLinks />
            </Box>
            <UpsellCarouselModule elevated={false} outlined={false} addMarginBottom={true} />
            <Box
              css={{
                background: 'bg.neutral',
                marginBottom: '400',
                padding: '{spacing.600} {spacing.600} {spacing.1000} {spacing.600}',
              }}
            >
              <StaticCommonQuestions />
            </Box>
          </Box>
        ) : (
          <SpinnerContainer />
        )}
      </Box>
    </>
  );
};

export default withRouter(PriceMyRentalResultsPage);
